import { find, flatten, flatMap } from "lodash/fp";
import { shallow } from "zustand/shallow";

import CitationGroup from "@app/analytics/TopicReport/components/CitationGroup";
import HighlightedEntity from "@app/analytics/TopicReport/components/HighlightedEntity";
import useTopicReportEntities from "@app/analytics/TopicReport/hooks/useTopicReportEntities";
import useTopicReportStore from "@app/analytics/TopicReport/hooks/useTopicReportStore";
import {
  getEntityTimelineItemId,
  groupCitationsBySource,
} from "@app/analytics/TopicReport/utils";
import type { TopicEntity } from "@app/analytics/types";

import { SourcesList } from "./styles";

const getCitationsForEntity = (entity: TopicEntity) => {
  return flatten([...entity.citations, flatMap("citations", entity.children)]);
};

const TopicReportSources = () => {
  const { allEntities, citations } = useTopicReportEntities();
  const { selectedEntityId, setActiveTimelineItemId, setSelectedEntityId } =
    useTopicReportStore(
      (state) => ({
        selectedEntityId: state.selectedEntityId,
        setActiveTimelineItemId: state.setActiveTimelineItemId,
        setSelectedEntityId: state.setSelectedEntityId,
      }),
      shallow
    );

  const selectedEntity = selectedEntityId
    ? find({ entityId: selectedEntityId }, allEntities)
    : undefined;

  const citationsToShow = selectedEntity
    ? getCitationsForEntity(selectedEntity)
    : citations;

  const groupedCitations = groupCitationsBySource(citationsToShow);

  const handleClickView = (entity: TopicEntity) => {
    setActiveTimelineItemId(getEntityTimelineItemId(entity));
  };

  return (
    <>
      <HighlightedEntity
        entity={selectedEntity}
        onClickView={handleClickView}
        onDismiss={() => setSelectedEntityId(null)}
      />
      <SourcesList>
        {groupedCitations.map((citationGroup) => {
          return (
            <CitationGroup
              key={citationGroup.groupName}
              {...citationGroup}
              selectedEntityId={selectedEntityId}
              forceExpand={selectedEntityId != null}
            />
          );
        })}
      </SourcesList>
    </>
  );
};

export default TopicReportSources;
